export enum FTSApplicationStatus {
  // NOTE: The enum values are the values of the `FTSAccountHolder` dictionary in the BE - JobStateHelpers.cs file.
  Pending = 'Pending',
  OnHold = 'On hold',
  InTranslation = 'In translation',
  ActionRequired = 'Action required',
  Completed = 'Completed',
  Cancelled = 'Cancelled',
}

export enum FTSCorrectionRequestStatus {
  Pending = 'Pending',
  Approved = 'Approved',
  InTranslation = 'In translation',
  Translated = 'Translated',
  Rejected = 'Rejected',
  Withdrawn = 'Withdrawn',
  Cancelled = 'Cancelled',
  Completed = 'Completed',
}

export const FTS_CORRECTION_REQUEST_END_STATUSES = [
  FTSCorrectionRequestStatus.Withdrawn,
  FTSCorrectionRequestStatus.Rejected,
  FTSCorrectionRequestStatus.Cancelled,
  FTSCorrectionRequestStatus.Completed,
];

export type StatusGroup = { [key: string]: string[] };

const FTS_APPLICANT_JOB_STATUS_GROUPS: StatusGroup = {
  PENDING: [FTSApplicationStatus.Pending],
  ON_HOLD: [FTSApplicationStatus.OnHold],
  IN_TRANSLATION: [FTSApplicationStatus.InTranslation],
  ACTION_REQUIRED: [FTSApplicationStatus.ActionRequired],
  COMPLETED: [FTSApplicationStatus.Completed],
  CANCELLED: [FTSApplicationStatus.Cancelled],
};

const FTS_CORRECTION_REQUEST_STATUS_GROUPS: StatusGroup = {
  PENDING: [FTSCorrectionRequestStatus.Pending],
  APPROVED: [FTSCorrectionRequestStatus.Approved],
  IN_TRANSLATION: [FTSCorrectionRequestStatus.InTranslation],
  TRANSLATED: [FTSCorrectionRequestStatus.Translated],
  WITHDRAWN: [FTSCorrectionRequestStatus.Withdrawn],
  REJECTED: [FTSCorrectionRequestStatus.Rejected],
  CANCELLED: [FTSCorrectionRequestStatus.Cancelled],
  COMPLETED: [FTSCorrectionRequestStatus.Completed],
};

export enum UniversalStatusGroupKey {
  // NOTE: The enum values are arbitrary, but have avoided setting them to the same values as the JobState enum above.
  PENDING = 1000,
  ON_HOLD,
  IN_PROGRESS,
  RETURNED_BY_FTS,
  COMPLETED,
  CALL_OUT_TO_USER,
}

/**
 * Maps the above job status groups to a universal status. This can then be used to identify the relevant status chip colour.
 */
export const FTS_APPLICANT_STATUS_GROUP_TO_UNIVERSAL_STATUS_MAPPING = new Map<
  UniversalStatusGroupKey,
  string[]
>();
FTS_APPLICANT_STATUS_GROUP_TO_UNIVERSAL_STATUS_MAPPING.set(
  UniversalStatusGroupKey.PENDING,
  FTS_APPLICANT_JOB_STATUS_GROUPS.PENDING
);
FTS_APPLICANT_STATUS_GROUP_TO_UNIVERSAL_STATUS_MAPPING.set(
  UniversalStatusGroupKey.ON_HOLD,
  FTS_APPLICANT_JOB_STATUS_GROUPS.ON_HOLD
);
FTS_APPLICANT_STATUS_GROUP_TO_UNIVERSAL_STATUS_MAPPING.set(
  UniversalStatusGroupKey.IN_PROGRESS,
  FTS_APPLICANT_JOB_STATUS_GROUPS.IN_TRANSLATION
);
FTS_APPLICANT_STATUS_GROUP_TO_UNIVERSAL_STATUS_MAPPING.set(
  UniversalStatusGroupKey.CALL_OUT_TO_USER,
  [
    ...FTS_APPLICANT_JOB_STATUS_GROUPS.ACTION_REQUIRED,
    ...FTS_APPLICANT_JOB_STATUS_GROUPS.CANCELLED,
  ]
);
FTS_APPLICANT_STATUS_GROUP_TO_UNIVERSAL_STATUS_MAPPING.set(
  UniversalStatusGroupKey.COMPLETED,
  FTS_APPLICANT_JOB_STATUS_GROUPS.COMPLETED
);

export const FTS_CORRECTION_REQUEST_STATUS_GROUP_TO_UNIVERSAL_STATUS_MAPPING =
  new Map<UniversalStatusGroupKey, string[]>();
FTS_CORRECTION_REQUEST_STATUS_GROUP_TO_UNIVERSAL_STATUS_MAPPING.set(
  UniversalStatusGroupKey.PENDING,
  FTS_CORRECTION_REQUEST_STATUS_GROUPS.PENDING
);
FTS_CORRECTION_REQUEST_STATUS_GROUP_TO_UNIVERSAL_STATUS_MAPPING.set(
  UniversalStatusGroupKey.IN_PROGRESS,
  [
    ...FTS_CORRECTION_REQUEST_STATUS_GROUPS.APPROVED,
    ...FTS_CORRECTION_REQUEST_STATUS_GROUPS.IN_TRANSLATION,
    ...FTS_CORRECTION_REQUEST_STATUS_GROUPS.TRANSLATED,
  ]
);
FTS_CORRECTION_REQUEST_STATUS_GROUP_TO_UNIVERSAL_STATUS_MAPPING.set(
  UniversalStatusGroupKey.CALL_OUT_TO_USER,
  [
    ...FTS_CORRECTION_REQUEST_STATUS_GROUPS.REJECTED,
    ...FTS_CORRECTION_REQUEST_STATUS_GROUPS.CANCELLED,
    ...FTS_CORRECTION_REQUEST_STATUS_GROUPS.WITHDRAWN,
  ]
);
FTS_CORRECTION_REQUEST_STATUS_GROUP_TO_UNIVERSAL_STATUS_MAPPING.set(
  UniversalStatusGroupKey.COMPLETED,
  FTS_CORRECTION_REQUEST_STATUS_GROUPS.COMPLETED
);

/**
 * Determines the universal status group key for the given job status code.
 * @param {Map<UniversalStatusGroupKey, string[]>} statusGroupToUniversalStatusMappingValues The mapping of job status code groups to universal status group keys.
 * @param {string} statusValueToFind The job status code to search for.
 * @returns {number | undefined} The matching universal status group key for the given status, else undefined.
 */
export const getUniversalStatusGroupKeyForStatus = (
  statusGroupToUniversalStatusMappingValues: Map<
    UniversalStatusGroupKey,
    string[]
  >,
  statusValueToFind: string
): UniversalStatusGroupKey | undefined => {
  const mappingArray = Array.from(
    statusGroupToUniversalStatusMappingValues,
    ([key, value]) => ({ key, value })
  );
  const foundValue = mappingArray.find((object) => {
    return object.value.find((value) => {
      return statusValueToFind === value;
    });
  });
  return foundValue?.key;
};
